class VanillaIvtnBoxFloating {
  constructor(
    boxSelector = "#js-ivtn-box",
    itemSelector = ".js-ivtn-box-itm",
    options = {},
  ) {
    this.boxSelector = boxSelector;
    this.itemSelector = itemSelector;

    this.minSpeed = options.minSpeed ?? 25;
    this.maxSpeed = options.maxSpeed ?? 65;
    this.padding = options.padding ?? 8;
    this.rotationRange = options.rotationRange ?? 18;

    // 767px以下の時の速度調整
    this.mobileBreakpoint = options.mobileBreakpoint ?? 767;
    this.mobileSpeedRatio = options.mobileSpeedRatio ?? 0.45;

    this.box = document.querySelector(this.boxSelector);
    this.items = this.box
      ? Array.from(this.box.querySelectorAll(this.itemSelector))
      : [];

    this.objects = [];
    this.boxWidth = 0;
    this.boxHeight = 0;
    this.animationId = null;
    this.lastTime = 0;

    this.mediaQuery = window.matchMedia(
      `(max-width: ${this.mobileBreakpoint}px)`,
    );

    this.animate = this.animate.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    if (!this.box || !this.items.length) return;

    this.updateBoxSize();
    this.createObjects();
    this.bindHoverEvents();

    window.addEventListener("resize", this.handleResize);

    this.animationId = window.requestAnimationFrame(this.animate);
  }

  updateBoxSize() {
    const rect = this.box.getBoundingClientRect();

    this.boxWidth = rect.width;
    this.boxHeight = rect.height;
  }

  createObjects() {
    this.objects = this.items.map((item) => {
      const itemWidth = item.offsetWidth;
      const itemHeight = item.offsetHeight;

      const angle = this.random(0, Math.PI * 2);
      const speed = this.random(this.minSpeed, this.maxSpeed);
      const rotate = this.random(-this.rotationRange, this.rotationRange);

      const rotatedSize = this.getRotatedSize(itemWidth, itemHeight, rotate);

      const object = {
        element: item,
        x: 0,
        y: 0,
        width: itemWidth,
        height: itemHeight,
        rotatedWidth: rotatedSize.width,
        rotatedHeight: rotatedSize.height,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotate,
        isPaused: false,
        handleMouseEnter: null,
        handleMouseLeave: null,
      };

      const bounds = this.getBounds(object);

      object.x = this.random(bounds.minX, bounds.maxX);
      object.y = this.random(bounds.minY, bounds.maxY);

      this.applyTransform(object);

      return object;
    });
  }

  bindHoverEvents() {
    this.objects.forEach((object) => {
      object.handleMouseEnter = () => {
        object.isPaused = true;
      };

      object.handleMouseLeave = () => {
        object.isPaused = false;
      };

      object.element.addEventListener(
        "mouseenter",
        object.handleMouseEnter,
        false,
      );

      object.element.addEventListener(
        "mouseleave",
        object.handleMouseLeave,
        false,
      );
    });
  }

  getRotatedSize(width, height, rotate) {
    const rad = Math.abs((rotate * Math.PI) / 180);
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));

    return {
      width: width * cos + height * sin,
      height: width * sin + height * cos,
    };
  }

  getBounds(object) {
    const extraX = Math.max(0, (object.rotatedWidth - object.width) / 2);
    const extraY = Math.max(0, (object.rotatedHeight - object.height) / 2);

    const minX = this.padding + extraX;
    const minY = this.padding + extraY;

    const maxX = this.boxWidth - object.width - this.padding - extraX;
    const maxY = this.boxHeight - object.height - this.padding - extraY;

    return {
      minX,
      minY,
      maxX: Math.max(minX, maxX),
      maxY: Math.max(minY, maxY),
    };
  }

  handleResize() {
    this.updateBoxSize();

    this.objects.forEach((object) => {
      object.width = object.element.offsetWidth;
      object.height = object.element.offsetHeight;

      const rotatedSize = this.getRotatedSize(
        object.width,
        object.height,
        object.rotate,
      );

      object.rotatedWidth = rotatedSize.width;
      object.rotatedHeight = rotatedSize.height;

      const bounds = this.getBounds(object);

      object.x = this.clamp(object.x, bounds.minX, bounds.maxX);
      object.y = this.clamp(object.y, bounds.minY, bounds.maxY);

      this.applyTransform(object);
    });
  }

  animate(time) {
    if (!this.lastTime) {
      this.lastTime = time;
    }

    const delta = Math.min((time - this.lastTime) / 1000, 0.05);
    const moveDelta = delta * this.getSpeedRatio();

    this.lastTime = time;

    this.objects.forEach((object) => {
      if (object.isPaused) return;

      const bounds = this.getBounds(object);

      object.x += object.vx * moveDelta;
      object.y += object.vy * moveDelta;

      if (object.x <= bounds.minX) {
        object.x = bounds.minX;
        object.vx *= -1;
      }

      if (object.x >= bounds.maxX) {
        object.x = bounds.maxX;
        object.vx *= -1;
      }

      if (object.y <= bounds.minY) {
        object.y = bounds.minY;
        object.vy *= -1;
      }

      if (object.y >= bounds.maxY) {
        object.y = bounds.maxY;
        object.vy *= -1;
      }

      this.applyTransform(object);
    });

    this.animationId = window.requestAnimationFrame(this.animate);
  }

  getSpeedRatio() {
    if (this.mediaQuery.matches) {
      return this.mobileSpeedRatio;
    }

    return 1;
  }

  applyTransform(object) {
    object.element.style.transform = `
      translate3d(${object.x}px, ${object.y}px, 0)
      rotate(${object.rotate}deg)
    `;
  }

  random(min, max) {
    if (max <= min) return min;

    return Math.random() * (max - min) + min;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  destroy() {
    if (this.animationId) {
      window.cancelAnimationFrame(this.animationId);
    }

    window.removeEventListener("resize", this.handleResize);

    this.objects.forEach((object) => {
      if (object.handleMouseEnter) {
        object.element.removeEventListener(
          "mouseenter",
          object.handleMouseEnter,
        );
      }

      if (object.handleMouseLeave) {
        object.element.removeEventListener(
          "mouseleave",
          object.handleMouseLeave,
        );
      }
    });

    this.objects = [];
  }
}

export default VanillaIvtnBoxFloating;
